// alarmRecognizeConfirm.js
// Confirmação direta do reconhecimento, a partir do aviso de alarme crítico.
//
// Antes o botão do aviso abria o histórico completo do ativo, e o operador
// tinha de localizar o alarme na tabela para só então confirmar. Três passos
// para uma ação que o aviso já identifica sozinho. Aqui vai direto à
// confirmação, que continua existindo porque reconhecer é uma escrita: registra
// no servidor que alguém tomou ciência da condição.
//
// O aviso é do ATIVO, não de uma variável: o nível crítico é derivado das
// variáveis por uma regra que vive fora do front-end. Por isso a confirmação
// abrange TODOS os alarmes abertos e não reconhecidos do ativo, e diz quantos
// são — reconhecer a condição do poço sem reconhecer o que a compõe deixaria
// pendência invisível.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useToken } from "../scripts/tokenContext";
import useApi from "../scripts/apiService";

const AlarmRecognizeConfirm = ({ show, notice, onHide, onRecognized }) => {
  const { t } = useTranslation();
  const { permissions } = useToken();
  const { fetchData, updateData } = useApi();

  // `useApi` devolve funções novas a cada render, fechando sobre o token
  // daquele render. Guardá-las em ref evita tanto congelar um token antigo
  // quanto refazer a busca a cada render.
  const apiRef = useRef({ fetchData, updateData });
  useEffect(() => {
    apiRef.current = { fetchData, updateData };
  });

  const [estado, setEstado] = useState("carregando");
  const [abertos, setAbertos] = useState([]);

  // Mesmo controle do histórico de alarmes: número 1, não string de bits.
  const podeReconhecer = permissions?.actions?.alarmsRecognize === 1;
  const deviceId = notice?.device?.id;

  useEffect(() => {
    if (!show || !deviceId) return undefined;
    let cancelado = false;
    setEstado("carregando");

    (async () => {
      try {
        const resposta = await apiRef.current.fetchData(
          `/devices/${deviceId}/list-alarms`,
        );
        if (cancelado) return;
        const lista = (resposta?.data || []).filter(
          (a) => a && !a.end_time && !a.recognized,
        );
        setAbertos(lista);
        setEstado(lista.length ? "pronto" : "vazio");
      } catch {
        if (!cancelado) setEstado("erro");
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [show, deviceId]);

  const confirmar = useCallback(async () => {
    setEstado("enviando");
    const resultados = await Promise.allSettled(
      abertos.map((a) =>
        apiRef.current.updateData(`/alarms/${a.id}/recognize`, {}),
      ),
    );

    // Uma falha parcial não pode encerrar o aviso: sobrou pendência que
    // ninguém reconheceu, e calar o alarme aqui a tornaria invisível.
    if (resultados.some((r) => r.status === "rejected")) {
      setEstado("falha");
      return;
    }

    onRecognized?.(abertos[0], { abertosPendentes: 0 });
    onHide();
  }, [abertos, onRecognized, onHide]);

  if (!notice) return null;

  const corpo = () => {
    if (!podeReconhecer) {
      return t("Você não tem permissão para reconhecer este alarme.");
    }
    if (estado === "carregando") return t("Carregando alarmes do ativo...");
    if (estado === "erro") {
      return t("Não foi possível consultar os alarmes deste ativo.");
    }
    if (estado === "falha") {
      return t("Não foi possível reconhecer. O alarme continua ativo.");
    }
    if (estado === "vazio") {
      return t("Nenhum alarme aberto e não reconhecido para este ativo.");
    }
    if (abertos.length > 1) {
      return `${t("Deseja reconhecer os")} ${abertos.length} ${t(
        "alarmes abertos deste ativo?",
      )}`;
    }
    return t("Deseja reconhecer este alarme?");
  };

  const podeConfirmar =
    podeReconhecer && (estado === "pronto" || estado === "falha");

  return (
    <Modal
      backdrop="static"
      show={show}
      onHide={onHide}
      centered
      data-testid="confirmacao-reconhecimento"
    >
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title>{t("Confirmação")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alarm-confirm-asset">{notice.name}</div>
        <div data-testid="confirmacao-mensagem">{corpo()}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Cancelar")}
        </Button>
        {podeConfirmar && (
          <Button
            variant="primary"
            onClick={confirmar}
            disabled={estado === "enviando"}
          >
            {t("Confirmar")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

AlarmRecognizeConfirm.propTypes = {
  show: PropTypes.bool,
  notice: PropTypes.object,
  onHide: PropTypes.func,
  onRecognized: PropTypes.func,
};

export default AlarmRecognizeConfirm;
